import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import ClearIcon from "@mui/icons-material/Clear";
import FilterListIcon from "@mui/icons-material/FilterList";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box, Button, Chip, CircularProgress, FormControl, IconButton, InputAdornment, InputLabel,
  MenuItem, Paper, Select, Table, TableBody, TableCell, TablePagination, TableRow,
  TextField, Tooltip, Typography,
} from "@mui/material";
import { useState } from "react";
import {
  DifficultyChip, FilterBarBox, PALETTE, StyledTableContainer, StyledTableHead, StyledTableRow, filterSelectSx,
} from "../components/shared";

const PAGE_SIZE_OPTIONS = [5, 10, 25, 50];

type Props = {
  problems: any[];
  facets: any;
  total: number;
  page: number;
  rowsPerPage?: number;
  activeProblemId?: string;
  filters: any;
  loading?: boolean;
  onApplyFilters: (f: any) => void;
  onPageChange: (p: number) => void;
  onRowsPerPageChange?: (n: number) => void;
  onLoadProblem: (id: string) => void;
  onToggleBookmark: (id: string) => void;
};

export default function ProblemExplorerView({
  problems, facets, total, page, rowsPerPage = 10, activeProblemId, filters, loading,
  onApplyFilters, onPageChange, onRowsPerPageChange, onLoadProblem, onToggleBookmark,
}: Props) {
  const [draft, setDraft] = useState(filters);

  const apply = (next: typeof draft) => { setDraft(next); onApplyFilters(next); };
  const clear = () => apply({ search: "", difficulty: "", topic: "", company: "", tag: "", language: "", status: "" });
  const hasFilters = Object.values(draft).some(Boolean);

  return (
    <Box sx={{ flexGrow: 1, px: 0.5 }}>
      {/* Filter bar */}
      <FilterBarBox sx={{ flexWrap: "nowrap", gap: 1, py: 1.25 }}>
        <FilterListIcon sx={{ fontSize: 15, color: "primary.main", flexShrink: 0 }} />
        <Typography sx={{ fontSize: "12px", fontWeight: 600, color: "primary.main", whiteSpace: "nowrap", flexShrink: 0 }}>
          Filters
        </Typography>

        <TextField
          size="small"
          placeholder="Search problems, tags..."
          value={draft.search}
          onChange={(e) => setDraft((p: any) => ({ ...p, search: e.target.value }))}
          onKeyDown={(e) => e.key === "Enter" && apply(draft)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 15, color: "text.disabled" }} />
              </InputAdornment>
            ),
            endAdornment: draft.search ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => apply({ ...draft, search: "" })}>
                  <ClearIcon sx={{ fontSize: 13 }} />
                </IconButton>
              </InputAdornment>
            ) : null,
            sx: { fontSize: "12px", borderRadius: "8px", bgcolor: "#fff" },
          }}
          sx={{
            flex: "1 1 140px", minWidth: 120, maxWidth: 200,
            "& .MuiOutlinedInput-notchedOutline": { borderColor: PALETTE.borderDark },
            "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#90caf9" },
          }}
        />

        {([["difficulty", "Difficulty", facets.difficulties ?? ["beginner", "intermediate", "advanced"]],
          ["topic", "Topic", facets.topics ?? []],
          ["company", "Company", facets.companies ?? []]] as const).map(([key, label, opts]) => (
          <FormControl size="small" sx={{ flex: "1 1 100px", minWidth: 90, maxWidth: 130 }} key={key}>
            <InputLabel sx={{ fontSize: "12px" }}>{label}</InputLabel>
            <Select
              label={label}
              value={(draft as any)[key] ?? ""}
              onChange={(e) => apply({ ...draft, [key]: e.target.value })}
              sx={{ ...filterSelectSx, fontSize: "12px" }}
              MenuProps={{ PaperProps: { style: { maxHeight: 220 } } }}
            >
              <MenuItem value="" sx={{ fontSize: "12px" }}>All</MenuItem>
              {opts.map((o: string) => (
                <MenuItem key={o} value={o} sx={{ fontSize: "12px", textTransform: "capitalize" }}>{o}</MenuItem>
              ))}
            </Select>
          </FormControl>
        ))}

        <FormControl size="small" sx={{ flex: "1 1 100px", minWidth: 90, maxWidth: 130 }} key="status">
          <InputLabel sx={{ fontSize: "12px" }}>Status</InputLabel>
          <Select
            label="Status"
            value={draft.status ?? ""}
            onChange={(e) => apply({ ...draft, status: e.target.value })}
            sx={{ ...filterSelectSx, fontSize: "12px" }}
            MenuProps={{ PaperProps: { style: { maxHeight: 220 } } }}
          >
            <MenuItem value="" sx={{ fontSize: "12px" }}>All</MenuItem>
            <MenuItem value="solved" sx={{ fontSize: "12px", textTransform: "capitalize" }}>Solved</MenuItem>
            <MenuItem value="unsolved" sx={{ fontSize: "12px", textTransform: "capitalize" }}>Unsolved</MenuItem>
          </Select>
        </FormControl>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: "auto", flexShrink: 0 }}>
          {!loading && (
            <Typography sx={{ fontSize: "12px", color: "text.disabled", whiteSpace: "nowrap" }}>
              {hasFilters ? `${total} matching problems` : `${total} problems`}
            </Typography>
          )}
          {hasFilters && (
            <Chip
              label="Clear"
              size="small"
              onDelete={clear}
              onClick={clear}
              sx={{ fontSize: "11px", bgcolor: "#fff0f0", color: "#c62828", borderColor: "#ffcdd2", "& .MuiChip-deleteIcon": { color: "#c62828" } }}
              variant="outlined"
            />
          )}
          <Button
            size="small"
            variant="contained"
            onClick={() => apply(draft)}
            sx={{ textTransform: "none", fontSize: "12px", height: "28px" }}
          >
            Search
          </Button>
        </Box>
      </FilterBarBox>

      {/* Table */}
      {loading ? (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", py: 8, gap: 2 }}>
          <CircularProgress size={36} />
          <Typography sx={{ color: "text.secondary", fontSize: "14px" }}>Loading problems...</Typography>
        </Box>
      ) : problems.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 8 }}>
          <Typography sx={{ color: "text.secondary", fontSize: "14px" }}>
            {hasFilters
              ? "No problems match your filters. Try adjusting or clearing them."
              : "No problems found."}
          </Typography>
        </Box>
      ) : (
        <Paper sx={{
          borderRadius: "12px",
          border: `1px solid ${PALETTE.border}`,
          boxShadow: "0px 1px 4px rgba(0,0,0,0.06)",
          overflow: "hidden",
        }}>
          <StyledTableContainer sx={{ maxHeight: "65vh" }}>
            <Table stickyHeader size="small" sx={{ tableLayout: "fixed", minWidth: 760 }}>
              <StyledTableHead>
                <TableRow>
                  <TableCell sx={{ width: "100px" }}>ID</TableCell>
                  <TableCell sx={{ width: "280px" }}>Problem</TableCell>
                  <TableCell sx={{ width: "120px" }}>Difficulty</TableCell>
                  <TableCell align="center" sx={{ width: "90px" }}>AC%</TableCell>
                  <TableCell align="center" sx={{ width: "110px" }}>Status</TableCell>
                  <TableCell align="center" sx={{ width: "160px" }}>Actions</TableCell>
                </TableRow>
              </StyledTableHead>
              <TableBody>
                {[...problems]
                  .sort((a, b) => {
                    const ORDER: Record<string, number> = { beginner: 0, intermediate: 1, advanced: 2 };
                    const da = ORDER[(a.difficulty ?? "").toLowerCase()] ?? 3;
                    const db = ORDER[(b.difficulty ?? "").toLowerCase()] ?? 3;
                    return da - db;
                  })
                  .map((p) => {
                    const id = p.id ?? p._id;
                    const isActive = id === activeProblemId;
                    const qid = p.qid || String(id).slice(-6).toUpperCase();
                    return (
                      <StyledTableRow
                        key={id}
                        sx={{ bgcolor: isActive ? "#eff6ff" : undefined, cursor: "pointer" }}
                        onClick={() => onLoadProblem(id)}
                      >
                        <TableCell sx={{ width: "100px", overflow: "hidden" }}>
                          <Chip
                            label={qid}
                            size="small"
                            variant="outlined"
                            sx={{
                              fontSize: "11px", bgcolor: "#f0f4ff", color: "#1976d2", fontWeight: 600,
                              maxWidth: "90px", justifyContent: "center",
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ width: "280px", maxWidth: "280px" }}>
                          <Tooltip title={p.title} placement="top">
                            <Typography sx={{
                              fontWeight: 600, fontSize: "13px",
                              color: p.solved ? "success.main" : "primary.main",
                              lineHeight: 1.3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                            }}>
                              {p.title}
                            </Typography>
                          </Tooltip>
                          {(p.tags ?? []).length > 0 && (
                            <Typography sx={{
                              fontSize: "11px", color: PALETTE.textMuted, mt: 0.25,
                              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                            }}>
                              {(p.tags ?? []).slice(0, 3).join(", ")}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ width: "120px" }}>
                          <DifficultyChip difficulty={p.difficulty} />
                        </TableCell>
                        <TableCell align="center" sx={{ width: "90px" }}>
                          <Typography sx={{ fontSize: "13px", fontWeight: 500 }}>
                            {p.acceptanceRate ?? 0}%
                          </Typography>
                        </TableCell>
                        <TableCell align="center" sx={{ width: "110px" }}>
                          {p.solved ? (
                            <Chip
                              size="small"
                              label="Solved"
                              sx={{
                                fontSize: "11px", height: "24px", px: 0.5,
                                bgcolor: "#f0fdf4", color: "#16a34a", fontWeight: 600,
                              }}
                            />
                          ) : (
                            <Chip
                              size="small"
                              label="Unsolved"
                              sx={{
                                fontSize: "11px", height: "24px", px: 0.5,
                                bgcolor: "#f8fafc", color: PALETTE.textMuted, fontWeight: 600,
                              }}
                            />
                          )}
                        </TableCell>
                        <TableCell align="center" sx={{ width: "160px" }} onClick={(e) => e.stopPropagation()}>
                          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 0.75 }}>
                            <Button
                              variant="contained"
                              color="primary"
                              size="small"
                              onClick={() => onLoadProblem(id)}
                              sx={{
                                textTransform: "none", fontSize: "11px", height: "26px",
                                minWidth: "88px", px: 1.25,
                              }}
                            >
                              {p.solved ? "Retake" : "Solve"} <ArrowForwardIcon sx={{ fontSize: 12, ml: 0.4 }} />
                            </Button>
                            <Tooltip title={p.bookmarked ? "Remove bookmark" : "Bookmark"}>
                              <IconButton
                                size="small"
                                onClick={() => onToggleBookmark(id)}
                                sx={{
                                  border: `1px solid ${PALETTE.borderDark}`,
                                  borderRadius: "8px",
                                  width: 28,
                                  height: 28,
                                }}
                              >
                                {p.bookmarked
                                  ? <BookmarkIcon color="primary" sx={{ fontSize: 16 }} />
                                  : <BookmarkBorderIcon sx={{ fontSize: 16 }} />}
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </StyledTableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </StyledTableContainer>

          <TablePagination
            component="div"
            count={total}
            page={page}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={PAGE_SIZE_OPTIONS}
            onPageChange={(_e, p) => onPageChange(p)}
            onRowsPerPageChange={(e) => {
              onRowsPerPageChange?.(parseInt(e.target.value, 10));
            }}
            sx={{
              borderTop: `1px solid ${PALETTE.border}`,
              bgcolor: PALETTE.bgLight,
              fontSize: "12px",
              "& .MuiTablePagination-toolbar": { minHeight: "40px" },
              "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": { fontSize: "12px" },
            }}
          />
        </Paper>
      )}
    </Box>
  );
}
